<script lang="ts">
  /**
   * OpenSetNotice — what the Scores tab says when a friend's set link arrives
   * through `swimset:open-set` (deepLink/openSet.ts). Sits right under the
   * Scores header so it is the first thing on screen after the native sheet
   * closes; arriving scrolls the page back to the top.
   *
   * Saved: one line ("Saved to my sets", or "Already in my sets" for a repeat),
   * then the set itself as the share card the friend sent (MySetCardImage: the
   * set in its stroke colours is the hero, ruling 10).
   * Refused: DeepLinkSheet's own title and hint (too new vs could not be read).
   * Both in the calm card chrome the rest of Scores uses, with a Dismiss.
   */
  import { tick } from "svelte";
  import { openSetArrival, OPEN_SET_PROBLEM_COPY } from "./openSet";
  import MySetCardImage from "../mySets/MySetCardImage.svelte";

  let arrival = $derived($openSetArrival);

  $effect(() => {
    if (!arrival) return;
    void arrival.at;
    void tick().then(() => window.scrollTo({ top: 0 }));
  });

  function dismiss() {
    openSetArrival.set(null);
  }
</script>

{#if arrival?.ok}
  {#if arrival.save.ok}
    <div class="open-set-notice" role="status" data-open-set="saved">
      <p class="title">{arrival.save.added ? "Saved to my sets" : "Already in my sets"}</p>
      <MySetCardImage card={arrival.card} />
      <button type="button" class="dismiss" onclick={dismiss}>Dismiss</button>
    </div>
  {:else}
    <div class="open-set-notice" role="alert" data-open-set="save-failed">
      <p class="title">This set could not be saved.</p>
      <p class="hint">Your phone refused to store it. Open the link again to try once more.</p>
      <MySetCardImage card={arrival.card} />
      <button type="button" class="dismiss" onclick={dismiss}>Dismiss</button>
    </div>
  {/if}
{:else if arrival}
  {@const copy = OPEN_SET_PROBLEM_COPY[arrival.problem]}
  <div class="open-set-notice" role="alert" data-open-set={arrival.problem}>
    <p class="title">{copy.title}</p>
    <p class="hint">{copy.hint}</p>
    <button type="button" class="dismiss" onclick={dismiss}>Dismiss</button>
  </div>
{/if}

<style>
  .open-set-notice {
    background: var(--bg-card);
    border: 1px solid var(--divider);
    border-radius: var(--radius-card);
    padding: var(--space-4);
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    line-height: 1.3;
    color: var(--text-primary);
  }
  .hint {
    margin: calc(-1 * var(--space-1)) 0 0;
    font-size: 15px;
    line-height: 1.45;
    color: var(--text-muted);
  }
  .dismiss {
    align-self: flex-start;
    min-height: 44px;
    margin: 0 calc(-1 * var(--space-2)) calc(-1 * var(--space-2));
    padding: var(--space-2);
    border: 0;
    background: transparent;
    font-family: inherit;
    font-size: 15px;
    font-weight: 600;
    color: var(--accent-primary);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  .dismiss:focus-visible {
    outline: 2px solid var(--accent-primary);
    outline-offset: 2px;
    border-radius: var(--radius-button);
  }
</style>
